import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { ClipLoader } from "react-spinners";
import Select from 'react-select';
import { debounce } from 'lodash';

export default function SSPIMaster() {
  // State management
  const [state, setState] = useState({
    classes: [],
    exams: [],
    subjects: [],
    sspiMasters: [],
    loading: true,
    formSubmitting: false,
    formData: {
      sspiMasterId: 0,
      sspiExamId: 0,
      sspiSubjectId: 0,
      classId: 0,
      sspiYear: new Date().getFullYear(),
      sspiIsActive: true,
      sspiStartDate: "",
      sspiEndDate: ""
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Clean up on modal close
  useEffect(() => {
    const modalElement = document.getElementById('sspiModal');
    const handleModalClose = () => {
      resetForm();
    };
    
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', handleModalClose);
    }
    
    return () => {
      if (modalElement) {
        modalElement.removeEventListener('hidden.bs.modal', handleModalClose);
      }
    };
  }, []);

  // API calls
  const fetchInitialData = async () => {
    try {
      updateState({ loading: true });
      
      // Fetch classes
      const classesRes = await axios.get(`${API_LoginURL}class/ClassMasterList`, { headers: getHeaders() });
      
      // Fetch exams
      const examsRes = await axios.get(`${API_LoginURL}Sspi/SspiExamList`, { headers: getHeaders() });
      const formattedExams = examsRes.data.map(exam => ({
        examId: exam.sspiExamId,
        examName: exam.sspiExamName
      }));
      
      // Fetch subjects with the correct API structure
      const subjectsRes = await axios.get(`${API_LoginURL}Sspi/SspiSubjectList`, { headers: getHeaders() });
      const formattedSubjects = subjectsRes.data.map(subject => ({
        subjectId: subject.sspiSubjectId,
        subjectName: subject.sspiSubject1,
        totalMarks: subject.sspiSubjectTotalMarks
      }));
      
      // Fetch SSPI Masters
      const sspiMastersRes = await axios.get(`${API_LoginURL}Sspi/SspiMasterList`, { headers: getHeaders() });
      
      // Enhance SSPI masters with names
      const enhancedMasters = Array.isArray(sspiMastersRes.data) ? sspiMastersRes.data.map(master => {
        const classInfo = classesRes.data.find(c => c.classId === master.classId);
        const examInfo = formattedExams.find(e => e.examId === master.sspiExamId);
        const subjectInfo = formattedSubjects.find(s => s.subjectId === master.sspiSubjectId);
        
        return {
          ...master,
          className: classInfo?.className || 'N/A',
          sspiExamName: examInfo?.examName || 'N/A',
          sspiSubjectName: subjectInfo?.subjectName || 'N/A',
          sspiIsActive: Boolean(master.sspiIsActive)
        };
      }) : [];
      
      updateState({
        classes: Array.isArray(classesRes.data) ? classesRes.data : [],
        exams: formattedExams,
        subjects: formattedSubjects,
        sspiMasters: enhancedMasters,
        loading: false
      });
    } catch (error) {
      console.error("Error fetching initial data:", error);
      Swal.fire("Error!", "Failed to load initial data.", "error");
      updateState({ loading: false });
    }
  };

  const fetchSSPIMasters = async () => {
    try {
      updateState({ loading: true });
      
      const response = await axios.get(`${API_LoginURL}Sspi/SspiMasterList`, {
        headers: getHeaders(),
      });
      
      // Ensure response.data is an array
      if (!Array.isArray(response.data)) {
        throw new Error("Invalid data format received from server");
      }
      
      // Enhance SSPI masters with names
      const enhancedMasters = response.data.map(master => {
        const classInfo = state.classes.find(c => c.classId === master.classId);
        const examInfo = state.exams.find(e => e.examId === master.sspiExamId);
        const subjectInfo = state.subjects.find(s => s.subjectId === master.sspiSubjectId);
        
        return {
          ...master,
          className: classInfo?.className || 'N/A',
          sspiExamName: examInfo?.examName || 'N/A',
          sspiSubjectName: subjectInfo?.subjectName || 'N/A',
          sspiIsActive: Boolean(master.sspiIsActive)
        };
      });
      
      updateState({
        sspiMasters: enhancedMasters,
        loading: false,
        currentPage: 0
      });
    } catch (error) {
      console.error("Error fetching SSPI Masters:", error);
      Swal.fire("Error!", "Failed to load SSPI Masters.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token")
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateState({
      formData: {
        ...state.formData,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  const handleSelectChange = (name, selectedOption) => {
    updateState({
      formData: {
        ...state.formData,
        [name]: selectedOption ? selectedOption.value : 0
      }
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      updateState({ searchQuery: query, currentPage: 0 });
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        sspiMasterId: 0,
        sspiExamId: 0,
        sspiSubjectId: 0,
        classId: 0,
        sspiYear: new Date().getFullYear(),
        sspiIsActive: true,
        sspiStartDate: "",
        sspiEndDate: ""
      }
    });
  };

  const handleEdit = (sspiMaster) => {
    // Format dates properly for input fields
    const formatDateForInput = (dateString) => {
      if (!dateString) return "";
      
      try {
        const date = new Date(dateString);
        // Check if date is valid
        if (isNaN(date.getTime())) return "";
        
        // Adjust for timezone offset
        const offset = date.getTimezoneOffset();
        const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
        
        return adjustedDate.toISOString().split('T')[0];
      } catch (error) {
        console.error("Error formatting date:", error);
        return "";
      }
    };

    updateState({ 
      formData: {
        sspiMasterId: sspiMaster.sspiMasterId || 0,
        sspiExamId: sspiMaster.sspiExamId || 0,
        sspiSubjectId: sspiMaster.sspiSubjectId || 0,
        classId: sspiMaster.classId || 0,
        sspiYear: sspiMaster.sspiYear || new Date().getFullYear(),
        sspiIsActive: Boolean(sspiMaster.sspiIsActive),
        sspiStartDate: formatDateForInput(sspiMaster.sspiStartDate),
        sspiEndDate: formatDateForInput(sspiMaster.sspiEndDate)
      }
    });
  };

  const handleToggleActive = async (sspiMasterId, currentIsActive) => {
    try {
      const newIsActive = !currentIsActive;
      
      await axios.post(
        `${API_LoginURL}Sspi/ToggleSspiActive`,
        { 
          sspiMasterId, 
          isActive: newIsActive 
        },
        { headers: getHeaders() }
      );
      
      // Update local state immediately for better UX
      updateState(prev => ({
        ...prev,
        sspiMasters: prev.sspiMasters.map(master =>
          master.sspiMasterId === sspiMasterId
            ? { ...master, sspiIsActive: newIsActive }
            : master
        )
      }));
      
      Swal.fire("Success!", "Status updated successfully.", "success");
    } catch (error) {
      console.error("Error toggling SSPI Master status:", error);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleDelete = async (sspiMasterId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await axios.post(
        `${API_LoginURL}Sspi/DeleteSspiMaster`,
        { sspiMasterId },
        { headers: getHeaders() }
      );

      // Check if deletion was successful
      if (response.data.response === "SUCCESS") {
        // Update state directly first for immediate UI update
        updateState(prev => ({
          ...prev,
          sspiMasters: prev.sspiMasters.filter(master => master.sspiMasterId !== sspiMasterId)
        }));
        
        // Then fetch to ensure consistency
        fetchSSPIMasters();
        
        Swal.fire("Deleted!", "SSPI Master has been deleted.", "success");
      } else if (response.data.response === "ERROR" && response.data.message) {
        // Show proper error message if deletion failed due to foreign key constraint
        Swal.fire({
          title: "Cannot Delete",
          html: `This SSPI Master cannot be deleted because it is linked to ${response.data.message}.<br><br>Please remove all associated data first.`,
          icon: "error",
          confirmButtonText: "OK"
        });
      } else {
        throw new Error(response.data.response || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting SSPI Master:", error);
      
      // Check for specific error messages
      if (error.response && error.response.data && error.response.data.message) {
        // Check for foreign key constraint errors
        const errorMsg = error.response.data.message.toLowerCase();
        if (errorMsg.includes('foreign key') || errorMsg.includes('constraint') || 
            errorMsg.includes('referenced') || errorMsg.includes('cannot delete')) {
          Swal.fire({
            title: "Cannot Delete",
            html: "This SSPI Master cannot be deleted because it is currently linked to other data (like student marks, assessments, etc.).<br><br>Please remove all associated records first.",
            icon: "error",
            confirmButtonText: "OK"
          });
        } else {
          Swal.fire("Error", error.response.data.message, "error");
        }
      } else if (error.response && error.response.status === 500) {
        Swal.fire({
          title: "Cannot Delete",
          html: "This SSPI Master is linked to other records and cannot be deleted.<br><br>Please remove all associated student marks, assessments, or other related data first.",
          icon: "error",
          confirmButtonText: "OK"
        });
      } else {
        Swal.fire("Error", "Failed to delete SSPI Master", "error");
      }
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    // Validate required fields
    const requiredFields = [
      { name: 'classId', label: 'Class' },
      { name: 'sspiExamId', label: 'Exam' },
      { name: 'sspiSubjectId', label: 'Subject' },
      { name: 'sspiYear', label: 'Year' },
      { name: 'sspiStartDate', label: 'Start Date' },
      { name: 'sspiEndDate', label: 'End Date' }
    ];
    
    const missingFields = requiredFields.filter(field => 
      !state.formData[field.name] || state.formData[field.name] === 0
    );
    
    if (missingFields.length > 0) {
      Swal.fire("Error", `Please fill all required fields: ${missingFields.map(f => f.label).join(', ')}`, "error");
      return;
    }

    // Validate date range
    const startDate = new Date(state.formData.sspiStartDate);
    const endDate = new Date(state.formData.sspiEndDate);
    
    if (startDate > endDate) {
      Swal.fire("Error", "End date must be after start date", "error");
      return;
    }

    updateState({ formSubmitting: true });
    try {
      const response = await axios.post(
        `${API_LoginURL}Sspi/SspiMasterAddEdit`,
        state.formData,
        { headers: getHeaders() }
      );

      if (response.data.response === "SUCCESS") {
        Swal.fire("Success", "SSPI Master saved successfully", "success");
        fetchSSPIMasters();
        
        // Close modal using Bootstrap's modal method
        const modalElement = document.getElementById('sspiModal');
        if (modalElement) {
          const modal = window.bootstrap.Modal.getInstance(modalElement);
          if (modal) {
            modal.hide();
          } else {
            // Fallback: trigger close button click
            document.getElementById("closeSSPIModal")?.click();
          }
        }
        
        resetForm();
      } else {
        Swal.fire("Error", response.data.response || "Failed to save SSPI Master", "error");
      }
    } catch (error) {
      console.error("Error saving SSPI Master:", error);
      Swal.fire("Error", "Failed to save SSPI Master", "error");
    } finally {
      updateState({ formSubmitting: false });
    }
  };

  // Filter and pagination
  const filteredSSPIMasters = state.sspiMasters.filter(item => {
    const matchesSearch = 
      (item.sspiExamName && item.sspiExamName.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (item.sspiSubjectName && item.sspiSubjectName.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (item.className && item.className.toLowerCase().includes(state.searchQuery.toLowerCase())) ||
      (item.sspiYear && item.sspiYear.toString().includes(state.searchQuery));
    
    const matchesStatus = 
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.sspiIsActive) ||
      (state.filterStatus === "inactive" && !item.sspiIsActive);
    
    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredSSPIMasters.length / state.itemsPerPage);
  const displayedSSPIMasters = filteredSSPIMasters.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return "Invalid Date";
    }
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">SSPI Master Management</h4>
                    
                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Exam, Subject, Class or Year"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow = "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>
                      
                      {/* Filter */}
                      <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: '120px' }}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      
                      {/* Add SSPI Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#sspiModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add SSPI</span>
                      </button>
                    </div>
                  </div>

                  {/* SSPI Master Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <ClipLoader color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading SSPI Masters...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="5%">ID</th>
                              <th width="15%">Class</th>
                              <th width="15%">Exam</th>
                              <th width="15%">Subject</th>
                              <th width="10%">Year</th>
                              <th width="15%">Start Date</th>
                              <th width="15%">End Date</th>
                              <th width="10%">Status</th>
                              <th width="10%" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedSSPIMasters.length > 0 ? (
                              displayedSSPIMasters.map((item) => (
                                <tr key={item.sspiMasterId}>
                                  <td>{item.sspiMasterId}</td>
                                  <td>{item.className}</td>
                                  <td>{item.sspiExamName}</td>
                                  <td>{item.sspiSubjectName}</td>
                                  <td>{item.sspiYear || "N/A"}</td>
                                  <td>{formatDate(item.sspiStartDate)}</td>
                                  <td>{formatDate(item.sspiEndDate)}</td>
                                  <td>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={item.sspiIsActive}
                                        onChange={() => handleToggleActive(item.sspiMasterId, item.sspiIsActive)}
                                        id={`toggle-${item.sspiMasterId}`}
                                      />
                                      <label 
                                        className="form-check-label" 
                                        htmlFor={`toggle-${item.sspiMasterId}`}
                                        style={{ color: item.sspiIsActive ? "green" : "red" }}
                                      >
                                        {item.sspiIsActive ? "Active" : "Inactive"}
                                      </label>
                                    </div>
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#sspiModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(item.sspiMasterId)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No SSPI Masters"
                                      style={{ opacity: 0.5, width: '60px' }}
                                    />
                                    <p className="mt-3 text-muted">No SSPI Masters found</p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#sspiModal"
                                      onClick={resetForm}
                                    >
                                      Add New SSPI Master
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedSSPIMasters.length > 0 && (
                          <ReactPaginate
                            previousLabel={<i className="fas fa-chevron-left"></i>}
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination justify-content-center mt-4"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SSPI Master Modal */}
      <div className="modal fade" id="sspiModal" tabIndex="-1" aria-labelledby="sspiModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="sspiModalLabel">
                {state.formData.sspiMasterId > 0 ? 'Edit SSPI Master' : 'Add New SSPI Master'}
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label htmlFor="classId" className="form-label required">Class</label>
                    <Select
                      id="classId"
                      options={state.classes.map(classItem => ({
                        value: classItem.classId,
                        label: classItem.className
                      }))}
                      value={state.classes
                        .map(classItem => ({
                          value: classItem.classId,
                          label: classItem.className
                        }))
                        .find(option => option.value === parseInt(state.formData.classId)) || null}
                      onChange={(selected) => handleSelectChange('classId', selected)}
                      placeholder="Select Class"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                  <div className="col-md-5 flex-grow-1 mb-3 ps-0">
                    <label htmlFor="sspiExamId" className="form-label required">Exam</label>
                    <Select
                      id="sspiExamId"
                      options={state.exams.map(exam => ({
                        value: exam.examId,
                        label: exam.examName
                      }))}
                      value={state.exams
                        .map(exam => ({
                          value: exam.examId,
                          label: exam.examName
                        }))
                        .find(option => option.value === parseInt(state.formData.sspiExamId)) || null}
                      onChange={(selected) => handleSelectChange('sspiExamId', selected)}
                      placeholder="Select Exam"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label htmlFor="sspiSubjectId" className="form-label required">Subject</label>
                    <Select
                      id="sspiSubjectId"
                      options={state.subjects.map(subject => ({
                        value: subject.subjectId,
                        label: subject.subjectName
                      }))}
                      value={ state.subjects
                        .map(subject => ({
                          value: subject.subjectId,
                          label: subject.subjectName
                        }))
                        .find(option => option.value === parseInt(state.formData.sspiSubjectId)) || null}
                      onChange={(selected) => handleSelectChange('sspiSubjectId', selected)}
                      placeholder="Select Subject"
                      className="basic-single"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      required
                    />
                  </div>
                  <div className="col-md-5 flex-grow-1 mb-3 ps-0">
                    <label htmlFor="sspiYear" className="form-label required">Year</label>
                    <input
                      type="number"
                      className="form-control p-2"
                      id="sspiYear"
                      name="sspiYear"
                      value={state.formData.sspiYear}
                      onChange={handleInputChange}
                      min="2000"
                      max="2100"
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3 pe-0">
                    <label htmlFor="sspiStartDate" className="form-label required">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="sspiStartDate"
                      name="sspiStartDate"
                      value={state.formData.sspiStartDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-5 flex-grow-1 mb-3 ps-0">
                    <label htmlFor="sspiEndDate" className="form-label required">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      id="sspiEndDate"
                      name="sspiEndDate"
                      value={state.formData.sspiEndDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                {state.formData.sspiMasterId > 0 && (
                  <div className="row">
                    <div className="mb-3 col-md-6 pe-0">
                      <label htmlFor="sspiIsActive" className="form-label">Status</label>
                      <select
                        className="form-select"
                        id="sspiIsActive"
                        name="sspiIsActive"
                        value={state.formData.sspiIsActive}
                        onChange={handleInputChange}
                      >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" id="closeSSPIModal" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}